import React, { useEffect, useMemo, useState } from "react";
import { RouteComponentProps, Router } from "@reach/router";
import * as R from "ramda";
import { useSetError } from "../redux/modals";
import { awaitJSON, MetricsLambdaFetch, S3SignedUrlFetch } from "../utils/fetch-utils";
import * as XLSX from "xlsx";
import { Dropdown, Page } from "../Components";
import { useTabbedNav } from "../utils/hooks/useNav";
import "./GeoMMM.scss";
import Results from "../GeoMMM/Results/Results";
import FAQ from "../MMM/FAQ/FAQ";
import { useCompanyInfo } from "../redux/company";
import { GeoMMMInfo } from "@blisspointmedia/bpm-types/dist/GeoMMM";
import { OUTCOME_TYPE_STRING } from "./DataFormatters";

const enum TabKey {
  RESULTS = "results",
  FAQ = "faq",
}

const NAVS = [
  { label: "Results", key: TabKey.RESULTS },
  { label: "FAQ", key: TabKey.FAQ },
];

const SHEET_NAMES = [
  "Summary Metrics",
  "MAPE by Geo",
  "Fit",
  "Media Spend",
  "Attribution Over Time",
  "ROI Table", // Includes info from Marginal ROI
  "Response Curves",
  "Adstock",
  "Attribution by Geo",
  "Grouping",
];

const GeoMMM = React.memo(({ navigate }: RouteComponentProps) => {
  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "geo-mmm",
    defaultKey: TabKey.RESULTS,
    navs: NAVS.map(nav => nav.key, NAVS),
  });
  const { cid: company } = useCompanyInfo();
  const setError = useSetError();
  const [groupBy, setGroupBy] = useState("channel");

  const [sourceFiles, setSourceFiles] = useState<GeoMMMInfo>();
  const [outcomeVariable, setOutcomeVariable] = useState<"revenue" | "kpiVolume">();
  const [outcomeVariableOptions, setOutcomeVariableOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  useEffect(() => {
    if (company && !sourceFiles) {
      (async () => {
        try {
          const res = await MetricsLambdaFetch("/getGeoMMMInfo", {
            params: {
              company,
            },
          });
          const parsedRes = await awaitJSON<GeoMMMInfo>(res);
          setSourceFiles(parsedRes);
          // Dashboard will be enabled for revenue and/or kpiVolume
          setOutcomeVariable(parsedRes.revenue.source ? "revenue" : "kpiVolume");
          const options = R.keys(parsedRes).map(key => {
            return { label: OUTCOME_TYPE_STRING[key], value: String(key) };
          });
          setOutcomeVariableOptions(options);
        } catch (e) {
          setError({
            message: e instanceof Error ? e.message : String(e),
          });
        }
      })();
    }
  }, [company, setError, sourceFiles]);

  // TODO: Programmatically set groupByOptions based data Groupings sheet
  const [groupByOptions, setGroupByOptions] = useState<Array<{ label: string; value: string }>>([]);
  useEffect(() => {
    if (!groupByOptions.length && ["tinuititest", "dsw", "onceuponafarm"].includes(company)) {
      setGroupByOptions([
        { label: "Channel", value: "channel" },
        { label: "Channel, Platform", value: "platform" },
      ]);
    }
    if (!groupByOptions.length && company === "olly") {
      setGroupByOptions([{ label: "Channel", value: "channel" }]);
    }
  }, [groupByOptions, company]);

  const [geoMMMData, setGeoMMMData] = useState<any>();
  useEffect(() => {
    if (sourceFiles && outcomeVariable && !geoMMMData) {
      (async () => {
        try {
          const s3Url = `${sourceFiles[outcomeVariable].source}`;
          const res = await S3SignedUrlFetch(s3Url);
          if (!res.ok) {
            throw new Error(`Failed to fetch data from URL: ${s3Url} - Status: ${res.status}`);
          }
          const arrayBuffer = await res.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, {
            type: "array",
            cellDates: true, // Temporary fix for Morgan's fake data where date is not formatted as string
          });
          const sheetJsons: Record<string, any[]> = {};
          SHEET_NAMES.forEach(name => {
            const worksheet = workbook.Sheets[name];
            if (!worksheet) {
              throw new Error(`Sheet "${name}" not found`);
            }
            sheetJsons[name] = XLSX.utils.sheet_to_json(worksheet, { defval: null });
          });
          setGeoMMMData(sheetJsons);
        } catch (e) {
          setError({
            message: e instanceof Error ? e.message : String(e),
          });
        }
      })();
    }
  }, [geoMMMData, outcomeVariable, setError, sourceFiles]);

  const [geoNames, setGeoNames] = useState<{ [dma: string]: string }>({});
  useEffect(() => {
    if (R.isEmpty(R.keys(geoNames))) {
      (async () => {
        try {
          let dmaData = await fetch("https://cdn.blisspointmedia.com/geo/DmaPaths.json");
          let dmaPaths = await dmaData.json();

          let dmaNamesMap: { [dma: string]: string } = {};
          R.keys(dmaPaths).forEach(dma => {
            dmaNamesMap[String(dma)] = dmaPaths[dma].name;
          });
          setGeoNames(dmaNamesMap);
        } catch (e) {
          setError({
            message: `Failed to get dma path information: ${
              e instanceof Error ? e.message : String(e)
            }`,
            reportError: e,
          });
        }
      })();
    }
  }, [geoNames, setError]);

  const {
    summaryMetricsRows,
    mapeByGeoRows,
    fitRows,
    mediaSpendRows,
    attributionOverTimeRows,
    roiTableRows,
    responseCurvesRows,
    adstockRows,
    attributionByGeoRows,
    channelBreakoutMap,
  } = useMemo(() => {
    const summaryMetricsRows: any[] = geoMMMData ? geoMMMData["Summary Metrics"] : [];
    const mapeByGeoRows: any[] = geoMMMData ? geoMMMData["MAPE by Geo"] : [];
    const fitRows: any[] = geoMMMData ? geoMMMData.Fit : [];
    const mediaSpendRows: any[] = geoMMMData
      ? geoMMMData["Media Spend"].filter(row => row.channel !== "All Channels")
      : [];
    const attributionOverTimeRows: any[] = geoMMMData ? geoMMMData["Attribution Over Time"] : [];
    const roiTableRows: any[] = geoMMMData
      ? geoMMMData["ROI Table"].filter(row => row.channel !== "All Channels")
      : [];
    const responseCurvesRows: any[] = geoMMMData
      ? geoMMMData["Response Curves"].filter(row => row.channel !== "All Channels")
      : [];
    const adstockRows: any[] = geoMMMData
      ? geoMMMData.Adstock.filter(row => row.channel !== "All Channels")
      : [];
    const attributionByGeoRows: any[] = geoMMMData
      ? geoMMMData["Attribution by Geo"].filter(row => row.channel !== "All Channels")
      : [];
    const channelBreakoutRows: any[] =
      geoMMMData?.Grouping.filter(row => row.channel !== "All Channels") || [];
    const mapping: Record<
      string,
      { channel: string; platform: string; tactic: string; brand_nonbrand: string }
    > = {};
    if (!R.isEmpty(channelBreakoutRows)) {
      channelBreakoutRows.forEach(entry => {
        const channel = entry.channel?.toLowerCase();
        const platform = entry.platform?.toLowerCase();
        const tactic = entry.tactic?.toLowerCase();
        const brand_nonbrand = entry.brand_nonbrand?.toLowerCase();
        const parts = [channel, platform, tactic, brand_nonbrand].filter(Boolean).join(", ");

        mapping[parts] = {
          channel,
          platform,
          tactic,
          brand_nonbrand,
        };
      });
    }

    return {
      summaryMetricsRows,
      mapeByGeoRows,
      fitRows,
      mediaSpendRows,
      attributionOverTimeRows,
      roiTableRows,
      responseCurvesRows,
      adstockRows,
      attributionByGeoRows,
      channelBreakoutMap: mapping,
    };
  }, [geoMMMData]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    if (
      outcomeVariable &&
      !R.isEmpty(summaryMetricsRows) &&
      !R.isEmpty(mapeByGeoRows) &&
      !R.isEmpty(fitRows) &&
      !R.isEmpty(mediaSpendRows) &&
      !R.isEmpty(attributionOverTimeRows) &&
      !R.isEmpty(roiTableRows) &&
      !R.isEmpty(responseCurvesRows) &&
      !R.isEmpty(adstockRows) &&
      !R.isEmpty(attributionByGeoRows) &&
      !R.isEmpty(R.keys(channelBreakoutMap)) &&
      !R.isEmpty(R.keys(geoNames))
    ) {
      setIsLoading(false);
    }
  }, [
    summaryMetricsRows,
    mapeByGeoRows,
    fitRows,
    mediaSpendRows,
    attributionOverTimeRows,
    roiTableRows,
    responseCurvesRows,
    adstockRows,
    attributionByGeoRows,
    channelBreakoutMap,
    geoNames,
    outcomeVariable,
  ]);

  return (
    <Page
      title="Geo MMM"
      pageType="Geo MMM"
      app2Redesign
      navs={NAVS}
      onNav={goToTab}
      selectedNav={tab}
      actions={
        tab === "results"
          ? [
              <Dropdown
                label="Outcome Variable"
                design="primary"
                background="dark"
                options={outcomeVariableOptions}
                onChange={value => {
                  setOutcomeVariable(value === "revenue" ? "revenue" : "kpiVolume");
                  setIsLoading(true);
                  setGeoMMMData(undefined);
                }}
                value={outcomeVariable}
                key="geoMMMOutcomeVariable"
                className="geoMMMOutcomeVariableDropdown"
              />,
              <Dropdown
                label="Group By"
                design="primary"
                background="dark"
                onChange={setGroupBy}
                options={groupByOptions}
                value={groupBy}
                key="geoMMMGroupBy"
              />,
            ]
          : []
      }
    >
      <Router className="fullPageRouter geoMMMPage">
        <Results
          path={TabKey.RESULTS}
          groupBy={groupBy}
          outcomeVariable={outcomeVariable}
          isLoading={isLoading}
          summaryMetricsRows={summaryMetricsRows}
          mapeByGeoRows={mapeByGeoRows}
          fitRows={fitRows}
          mediaSpendRows={mediaSpendRows}
          channelBreakoutMap={channelBreakoutMap}
          geoNames={geoNames}
          attributionOverTimeRows={attributionOverTimeRows}
          roiTableRows={roiTableRows}
          responseCurvesRows={responseCurvesRows}
          adstockRows={adstockRows}
          attributionByGeoRows={attributionByGeoRows}
        />
        <FAQ path={TabKey.FAQ} geoMMM={true} />
      </Router>
    </Page>
  );
});

export default GeoMMM;
